import React from 'react';
import { Filter, Link as LinkIcon, User, CheckCircle2, ChevronRight, Trophy, Plus, Minus } from 'lucide-react';

const Matches = ({ 
  matches, 
  players, 
  selectedFilters, 
  toggleFilter, 
  setSelectedFilters, 
  partnerMapping, 
  updateScore, 
  setWinner, 
  getMatchTime, 
  formatDuration, 
  setActiveMatchId, 
  setView 
}) => (
  <div className="space-y-6 animate-in fade-in duration-500 pb-12">
    <section className="bg-slate-900 rounded-3xl border border-slate-800 p-5 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-emerald-500" />
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Filter by Athlete:</h3>
        </div>
        {selectedFilters.length > 0 && (
          <button onClick={() => setSelectedFilters([])} className="text-[10px] text-rose-500 font-bold hover:underline">Clear</button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {[...players].sort().map(p => (
          <button key={p} onClick={() => toggleFilter(p)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${selectedFilters.includes(p) ? 'bg-emerald-500 border-emerald-400 text-slate-950 shadow-lg shadow-emerald-500/10' : 'bg-slate-950 border-slate-800 text-slate-500'}`}>{p}</button>
        ))}
      </div>
    </section>

    {selectedFilters.length > 0 && partnerMapping && (
      <section className="animate-in slide-in-from-top-4 duration-300">
        <div className="flex items-center gap-2 mb-3 px-1">
          <LinkIcon className="w-3 h-3 text-emerald-500" />
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500">Confirmed Partnerships:</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {selectedFilters.map(filteredPlayer => (
            <div key={filteredPlayer} className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3 border-b border-emerald-500/10 pb-2">
                <User className="w-3 h-3 text-emerald-500" />
                <span className="text-xs font-black text-emerald-400 uppercase truncate">{filteredPlayer}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {partnerMapping[filteredPlayer]?.length > 0 ? (
                  partnerMapping[filteredPlayer].map(partner => (
                    <span key={partner} className="bg-slate-950 border border-slate-800 text-[10px] font-bold text-slate-300 px-2 py-1 rounded-lg">
                      {partner}
                    </span>
                  ))
                ) : (
                  <span className="text-[10px] text-slate-600 italic uppercase">No partners</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    )}

    <div className="space-y-4">
      <div className="flex items-center justify-between px-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
          <span>Clashes ({matches.length})</span>
      </div>
      {matches.map((m) => (
        <div key={m.id} className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl transition-transform active:scale-[0.99] group">
          <div 
            onClick={() => { setActiveMatchId(m.id); setView('match_detail'); }}
            className="bg-slate-800/30 px-4 sm:px-6 py-3 flex justify-between items-center border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em]">Game #{m.id}</span>
              {m.completed && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
            </div>
            <div className="flex items-center gap-3">
              {m.firstStartTime && <div className="text-[10px] text-slate-500 font-bold tabular-nums">{formatDuration(getMatchTime(m))}</div>}
              <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-500 transition-colors" />
            </div>
          </div>
          
          <div className="p-2 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-4">
            <div className={`flex-1 text-left px-3 py-4 rounded-2xl transition-all ${m.winner === 1 ? 'bg-emerald-500/10' : ''}`}>
              <div className={`text-lg sm:text-xl font-black leading-tight uppercase tracking-tighter ${selectedFilters.some(f => m.pair1.includes(f)) ? 'text-emerald-400 underline decoration-emerald-500/40 underline-offset-4' : 'text-slate-100'}`}>
                {m.pair1[0]} <br/> {m.pair1[1]}
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 bg-slate-950/80 p-2 rounded-2xl border border-slate-800 shrink-0 z-10 w-full sm:w-auto">
              <div className="flex items-center gap-4 sm:gap-6" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setWinner(m.id, 1)} className={`p-2 sm:p-2.5 rounded-xl transition-all border-2 ${m.winner === 1 ? 'bg-emerald-500 border-emerald-400 text-slate-950 scale-110 shadow-lg shadow-emerald-500/40' : 'bg-slate-900 border-slate-800 text-slate-700 hover:text-slate-400'}`}>
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <div className="w-[1px] h-6 bg-slate-800"></div>
                <button onClick={() => setWinner(m.id, 2)} className={`p-2 sm:p-2.5 rounded-xl transition-all border-2 ${m.winner === 2 ? 'bg-emerald-500 border-emerald-400 text-slate-950 scale-110 shadow-lg shadow-emerald-500/40' : 'bg-slate-900 border-slate-800 text-slate-700 hover:text-slate-400'}`}>
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
              <div className="flex items-center gap-2 sm:gap-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col gap-1">
                  <button onClick={() => updateScore(m.id, 1, 1)} className="p-1 sm:p-1.5 text-emerald-500 hover:bg-emerald-500/10 rounded-lg"><Plus className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                  <button onClick={() => updateScore(m.id, 1, -1)} className="p-1 sm:p-1.5 text-rose-500 hover:bg-rose-500/10 rounded-lg"><Minus className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                </div>
                <div className={`text-3xl sm:text-4xl font-black font-mono tracking-tighter tabular-nums flex items-center gap-1 sm:gap-2 ${m.score1 < 0 || m.score2 < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  <span>{m.score1}</span>
                  <span className="text-slate-800 text-xl sm:text-2xl">:</span>
                  <span>{m.score2}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => updateScore(m.id, 2, 1)} className="p-1 sm:p-1.5 text-emerald-500 hover:bg-emerald-500/10 rounded-lg"><Plus className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                  <button onClick={() => updateScore(m.id, 2, -1)} className="p-1 sm:p-1.5 text-rose-500 hover:bg-rose-500/10 rounded-lg"><Minus className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                </div>
              </div>
            </div>

            <div className={`flex-1 text-right px-3 py-4 rounded-2xl transition-all ${m.winner === 2 ? 'bg-emerald-500/10' : ''}`}>
              <div className={`text-lg sm:text-xl font-black leading-tight uppercase tracking-tighter ${selectedFilters.some(f => m.pair2.includes(f)) ? 'text-emerald-400 underline decoration-emerald-500/40 underline-offset-4' : 'text-slate-100'}`}>
                {m.pair2[0]} <br/> {m.pair2[1]}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default Matches;
